import type { Dispatch } from 'react';

import { computeSHA256 } from '@/lib/hash';
import { uploadFile } from '@/services/fileService';

import type { Action } from './batchUploadReducer';
import type { UploadItem } from './types';

/** Process a single file through the hash + upload pipeline. */
export async function processFile(
  item: UploadItem,
  docClass: string | undefined,
  dispatch: Dispatch<Action>,
): Promise<void> {
  const { id, file, abortController } = item;

  // Guard: item may have been cancelled while waiting in the pool queue
  if (abortController.signal.aborted) return;

  try {
    // Phase 1: Hash. Compute it HERE (not inside uploadFile) and await it
    // between the two dispatches — otherwise React batches START_HASH +
    // START_UPLOAD into one render and the "Hashing" state never paints,
    // so a large file's multi-second digest shows as "uploading 0%" (reads
    // as stuck — part of the AW-42 complaint).
    dispatch({ type: 'START_HASH', id });
    const contentHash = await computeSHA256(file);
    if (abortController.signal.aborted) return;

    // Phase 2: Upload (initiate + GCS PUT + confirm), reusing the hash.
    dispatch({ type: 'START_UPLOAD', id });
    const result = await uploadFile(
      file,
      docClass,
      (progress) => dispatch({ type: 'UPDATE_PROGRESS', id, progress }),
      abortController.signal,
      contentHash,
      item.uploadBatchId,
    );

    if (result.isDuplicate) {
      dispatch({ type: 'MARK_DUPLICATE', id, result });
    } else {
      // bytesReused → COMPLETE without further progress events; the
      // backend has already published file.uploaded. The UI flows
      // straight to processing/parsed without a GCS PUT step.
      dispatch({ type: 'COMPLETE', id, result });
    }
  } catch (err: unknown) {
    // Cancelled by user — state already set by CANCEL_ITEM action
    if (err instanceof DOMException && err.name === 'AbortError') return;
    if (err instanceof Error && err.name === 'CanceledError') return;

    const message =
      err instanceof Error ? err.message : 'Upload failed';
    dispatch({ type: 'FAIL', id, error: message });
  }
}
